// apps/web/src/lib/painel/home/carga-home.ts — Fase 17.8, Plano 01 (D-01, D-13).
//
// Carga da home de um perfil: cada perfil busca o seu endpoint de forma independente, com timeout, e o resultado é sempre
// um valor (sucesso, sem-acesso, nao-autenticado ou erro), nunca uma exceção. O 401 é distinto do 403: a recarga ao vivo
// precisa parar e levar a pessoa ao login quando a sessão expira.
import type { PainelHomeResponsePorPerfil, PerfilHome, PerfilHomeComResposta } from '@pieltec/contracts';

export type ResultadoHome<T> =
  | { readonly tipo: 'sucesso'; readonly dados: T }
  | { readonly tipo: 'sem-acesso' }
  | { readonly tipo: 'nao-autenticado' }
  | { readonly tipo: 'erro' };

export const TIMEOUT_CARGA_HOME_MS = 15000;

/** Caminho do endpoint de home de um perfil. */
export function caminhoHome(perfil: PerfilHome): string {
  return `/api/v1/painel/home/${perfil}`;
}

/** GET /api/v1/painel/home/{perfil}. 401 vira `nao-autenticado`, 403 `sem-acesso`; qualquer outra falha (inclusive timeout) vira `erro`. */
export async function carregarHome<P extends PerfilHomeComResposta>(
  perfil: P,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = TIMEOUT_CARGA_HOME_MS,
): Promise<ResultadoHome<PainelHomeResponsePorPerfil[P]>> {
  try {
    const resposta = await fetchImpl(caminhoHome(perfil), { credentials: 'include', signal: AbortSignal.timeout(timeoutMs) });
    if (resposta.status === 401) return { tipo: 'nao-autenticado' };
    if (resposta.status === 403) return { tipo: 'sem-acesso' };
    if (!resposta.ok) return { tipo: 'erro' };
    const dados = (await resposta.json()) as PainelHomeResponsePorPerfil[P];
    return { tipo: 'sucesso', dados };
  } catch {
    return { tipo: 'erro' };
  }
}
